import { useRef, useEffect } from 'react';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { tooltipStyle, COLORS } from '../../utils/colors';
import { useColorScheme } from '../../hooks/useColorScheme';

/**
 * Horizontal bar chart showing % of respondents per option (Layout B - Single Choice).
 *
 * Props:
 *   data — [{ optionText: "Yes", count: 1542 }, { optionText: "No", count: 40 }, ...]
 */
export default function SingleChoiceChart({ data }) {
  const { palette } = useColorScheme();
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current || !data) return;

    if (chartRef.current) chartRef.current.destroy();

    const total = data.reduce((s, d) => s + d.count, 0);
    const labels = data.map(d => truncateLabel(d.optionText, 45));
    const fullLabels = data.map(d => d.optionText);
    const pcts = data.map(d => total > 0 ? (d.count / total * 100) : 0);

    chartRef.current = new Chart(canvasRef.current, {
      type: 'bar',
      data: {
        labels,
        datasets: [{
          label: '% of Respondents',
          data: pcts,
          backgroundColor: pcts.map((_, i) => palette[i % palette.length]),
          borderWidth: 0,
          borderRadius: 6,
          barPercentage: 0.6,
          categoryPercentage: 0.8
        }]
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        _fullLabels: fullLabels,
        scales: {
          x: {
            beginAtZero: true,
            max: 100,
            grid: { color: '#f0f1f3' },
            ticks: {
              font: { size: 11 },
              color: COLORS.textMuted,
              callback: (v) => v + '%'
            }
          },
          y: {
            grid: { display: false },
            ticks: {
              font: { size: 12, weight: 500 },
              color: COLORS.textSecondary,
              padding: 8
            }
          }
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            ...tooltipStyle,
            callbacks: {
              title: (items) => fullLabels[items[0].dataIndex],
              label: (item) => ` ${item.raw.toFixed(1)}% of respondents`
            }
          },
          datalabels: {
            display: true,
            anchor: 'end',
            align: 'right',
            color: COLORS.textSecondary,
            font: { size: 11, weight: 600 },
            formatter: (v) => v.toFixed(1) + '%'
          }
        }
      }
    });

    return () => { if (chartRef.current) chartRef.current.destroy(); };
  }, [data, palette]);

  return <canvas ref={canvasRef} />;
}

function truncateLabel(text, maxLen) {
  if (!text) return '';
  return text.length > maxLen ? text.slice(0, maxLen - 3) + '...' : text;
}
